import React from 'react';
import {
  HealthProfileAccordionData,
  PatientProfileAccordionData,
} from '../../_components/organisms/PatientDetailsSideContent';
import { numberToCurrencyFormatter } from '../helper-utils/helper';
import { DottedText } from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';

interface IPatientDetails {
  patientName: string;
  gender: string;
  age: number;
  memberRelation: string;
}

interface IEncounters {
  outpatient_utilization: number;
  inpatient_utilization: number;
  emergency_utilization: number;
  urgent_care_utilization: number;
}

interface IPatientDetailsTotalCost {
  serviceYear: number;
  totalPaidAamount: number;
}

interface IRecentVisit {
  date: String;
  service: String;
}

interface IHealthProfileDetailsRiskTable {
  condition: string;
  probability: number;
}

interface IHealthProfileDetails {
  current_conditions: string[];
  risk_table: IHealthProfileDetailsRiskTable[];
}
interface IEnrollmentDetails {
  plan: string;
  effectiveDate: string;
  terminationDate: string;
  termination_flag: boolean;
}

const hospitalVisitDataKeyMap = new Map<string, number>();
hospitalVisitDataKeyMap.set('inpatient_utilization', 0);
hospitalVisitDataKeyMap.set('outpatient_utilization', 1);
hospitalVisitDataKeyMap.set('emergency_utilization', 2);
hospitalVisitDataKeyMap.set('urgent_care_utilization', 3);

const enrollmentDataKeyMap = new Map<string, number>();
enrollmentDataKeyMap.set('plan', 0);
enrollmentDataKeyMap.set('effectiveDate', 1);
enrollmentDataKeyMap.set('terminationDate', 2);

const tableEmptyStateData = [['None', 'None']];

const getPatientDetails = (data: IPatientDetails, member_id: string) => {
  return [
    data.patientName || member_id,
    data?.age?.toString() ?? '-',
    data?.gender ?? '-',
  ];
};

const getPatientHospitalVisitData = (data: IEncounters[]) => {
  const hospitalVisitDataArray = [
    ['Inpatient', 0],
    ['Outpatient', 0],
    ['Emergency', 0],
    ['Urgent Care', 0],
  ];

  data
    ? Object.entries(data)?.forEach((item: any) => {
        const keyIndex = hospitalVisitDataKeyMap.get(item[0]) ?? 3;
        hospitalVisitDataArray[keyIndex][1] = item[1];
      })
    : hospitalVisitDataArray;

  return hospitalVisitDataArray;
};

const getEnrollmentData = (data: IEnrollmentDetails, handleOnClick: any) => {
  const enrollmentDataArray = [
    [
      'Plan',
      <div onClick={handleOnClick}>
        <DottedText
          text={data?.plan ?? ''}
          typographyStyles={{
            fontSize: '12px',
            lineHeight: '24px',
            fontWeight: 500,
            color: patientAccessTheme.palette.primary[500],
          }}
        />
      </div>,
    ],
    ['Effective Date', data?.effectiveDate ?? ''],
    [
      'Termination Date',
      !data?.termination_flag ? '-' : data?.terminationDate ?? '',
    ],
  ];

  // data
  //   ? Object.entries(data)?.forEach((item: any) => {
  //       const keyIndex = enrollmentDataKeyMap.get(item[0]) ?? 3;
  //       enrollmentDataArray[keyIndex][1] = item[1];
  //     })
  //   : enrollmentDataArray;

  return enrollmentDataArray;
};

const getPatientTotalCostData = (data: IPatientDetailsTotalCost[]) => {
  if (data.length === 0) {
    return tableEmptyStateData;
  }
  return data
    ? data?.map((item: IPatientDetailsTotalCost) => {
        return [
          item?.serviceYear,
          numberToCurrencyFormatter(item?.totalPaidAamount),
        ];
      })
    : tableEmptyStateData;
};

const getPatientRecentVisitData = (data: IRecentVisit) => {
  return data ? [[data?.date, data?.service]] : tableEmptyStateData;
};

export const getPatientProfileAccordionData = (
  data: {
    patientDetails: IPatientDetails;
    encounters: IEncounters[];
    totalCost: IPatientDetailsTotalCost[];
    recentVisit: IRecentVisit;
    memberRelationShipData: string;
    enrollmentData: IEnrollmentDetails;
  },
  member_id: string,
  handleOnClickPlan: any,
) => {
  const patientProfileAccordionData: PatientProfileAccordionData = {
    values: ['-', '-', '-'],
    hospitalVisitData: [
      ['Inpatient', 0],
      ['Outpatient', 0],
      ['Emergency', 0],
      ['Office Visit ', 0],
      ['Urgent Care', 0],
    ],
    totalCostData: tableEmptyStateData,
    recentVisitData: tableEmptyStateData,
    memberRelationShipData: '',
    enrollmentData: [
      ['Plan', ''],
      ['Effective Date', ''],
      ['Termination Date', ''],
    ],
  };

  patientProfileAccordionData.values = getPatientDetails(
    data?.patientDetails,
    member_id,
  );
  patientProfileAccordionData.hospitalVisitData = getPatientHospitalVisitData(
    data?.encounters,
  );
  patientProfileAccordionData.recentVisitData = getPatientRecentVisitData(
    data?.recentVisit,
  );
  patientProfileAccordionData.totalCostData = getPatientTotalCostData(
    data?.totalCost,
  );
  patientProfileAccordionData.memberRelationShipData =
    data?.patientDetails?.memberRelation;

  patientProfileAccordionData.enrollmentData = getEnrollmentData(
    data?.enrollmentData,
    handleOnClickPlan,
  );
  return patientProfileAccordionData;
};

const getHealthProfileCurrentCondition = (current_conditions: string[]) => {
  if (typeof current_conditions === 'number') {
    return '-';
  }
  return current_conditions.join(' | ');
};

const getHealthProfileLatentRiskData = (
  risk_table: IHealthProfileDetailsRiskTable[],
) => {
  if (typeof risk_table === 'number') {
    return tableEmptyStateData;
  }
  return risk_table
    ? risk_table?.map((item: IHealthProfileDetailsRiskTable) => {
        return [item?.condition, item?.probability];
      })
    : tableEmptyStateData;
};

export const getHealthProfileAccordionData = (data: IHealthProfileDetails) => {
  const healthProfileAccordionData: HealthProfileAccordionData = {
    currentCondition: '-',
    latentRiskData: tableEmptyStateData,
  };

  healthProfileAccordionData.currentCondition = getHealthProfileCurrentCondition(
    data?.current_conditions,
  );
  healthProfileAccordionData.latentRiskData = getHealthProfileLatentRiskData(
    data?.risk_table,
  );

  return healthProfileAccordionData;
};
